import React, { useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import DataTable from "../../../../components/DataTable";
import EmptyValue from "../../../../components/EmptyValue";
import useMedicineStock from "../../../hooks/useMedicineStock";
import { getViewDemand, getViewPurchase, stripsReadout, UnavailableBadge } from "../shared";

// The cart line's info panel (2026-10-30): one medicine's eVital stock, batch by batch, from ONE
// get_batches call (GET /api/inventory/medicine-stock) — read only when the panel is opened, so
// a big cart costs nothing until the pharmacist asks. Shows every batch, which of them count
// toward stock and why the rest don't, and the purchase quantity recomputed from that exact
// stock (the listing's figure is eVital's bulk total, near-expiry batches included).
// "Re-read stock" asks eVital again after a correction there; "Use suggested" (cart only) copies
// the exact figure into the line's quantity.

const Shimmer = ({ className }) => <div className={`animate-pulse rounded bg-[var(--ops-carbon-100)] ${className}`} />;
Shimmer.propTypes = { className: PropTypes.string.isRequired };

function BatchTableSkeleton() {
    return (
        <div role="status" aria-live="polite" aria-label="Reading stock from eVital">
            <div className="mb-2 flex items-center gap-2 text-sm text-[var(--ops-carbon-500)]">
                <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-[var(--ops-carbon-200)] border-t-[var(--ops-emerald-500)]" />
                Reading stock from eVital…
            </div>
            <div className="rounded-xl border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)] p-3 space-y-3">
                {[0, 1, 2].map((i) => (
                    <div key={i} className="grid grid-cols-4 gap-4">
                        <Shimmer className="h-3.5" />
                        <Shimmer className="h-3.5 w-1/2" />
                        <Shimmer className="h-3.5 w-1/3" />
                        <Shimmer className="h-3.5 w-2/3" />
                    </div>
                ))}
            </div>
        </div>
    );
}

const batchColumns = [
    { key: "batch", label: "Batch", render: (batch) => batch.batch || <EmptyValue /> },
    // MM/YY — how expiry is printed on strips and quoted in the trade (eVital's own label).
    { key: "expiry", label: "Expiry", render: (batch) => (batch.expiry ? moment(batch.expiry, "YYYY-MM-DD").format("MM/YY") : <EmptyValue />) },
    { key: "quantity", label: "Qty", render: (batch) => batch.quantity },
    {
        key: "counted",
        label: "Counted in stock",
        render: (batch) => (batch.counted
            ? <span className="text-[var(--ops-emerald-600)] font-semibold">Yes</span>
            : <span className="text-[var(--ops-carbon-500)]">No — {batch.note}</span>),
    },
];

function BatchStockPanel({ line, onUseSuggested }) {
    const { data: stock, isLoading, isError, isFetching, refetch } = useMedicineStock(line.itemInventoryId, { lookaheadDays: line.lookaheadDays });
    // eVital keeps every batch a medicine ever had, most of them at 0 — hidden by default.
    const [showEmptyBatches, setShowEmptyBatches] = useState(false);

    const exactRow = stock?.row || null;
    const canSuggest = exactRow && !exactRow.stockUnknown && !exactRow.demandUnknown;
    const suggested = canSuggest ? getViewPurchase(exactRow, line.view, line.lapsingEnabled).tablets ?? exactRow.tabletsToOrder : null;

    const coverageEnd = stock?.coverageWindowDays != null
        ? moment().add(stock.coverageWindowDays, "days").format("D MMM YYYY")
        : null;
    const allBatches = stock?.batches || [];
    const emptyBatchCount = allBatches.filter((batch) => batch.quantity <= 0).length;
    const shownBatches = showEmptyBatches ? allBatches : allBatches.filter((batch) => batch.quantity > 0);

    let body;
    if (isLoading) {
        body = <BatchTableSkeleton />;
    } else if (isError) {
        body = <p className="text-sm text-[var(--ops-crimson-600)]">Couldn&apos;t read stock from eVital. Try &ldquo;Re-read stock&rdquo;.</p>;
    } else if (stock.onHand === null) {
        body = <UnavailableBadge row={{ stockUnknown: true, stockUnknownReason: stock.stockUnknownReason }} />;
    } else if (allBatches.length === 0) {
        body = <p className="text-sm text-[var(--ops-carbon-500)]">eVital has no batches for this medicine — stock is 0.</p>;
    } else if (shownBatches.length === 0) {
        body = <p className="text-sm text-[var(--ops-carbon-500)]">Every batch in eVital is at 0 — stock is 0.</p>;
    } else {
        body = (
            <>
                <DataTable columns={batchColumns} rows={shownBatches.map((batch, i) => ({ ...batch, id: `${batch.batch}-${i}` }))} />
                {coverageEnd && (
                    <p className="mt-2 text-xs text-[var(--ops-carbon-500)]">
                        Only batches expiring after {coverageEnd} count; eVital&apos;s sample batch never does. Check these against the shelf — if eVital is wrong, correct it there and re-read.
                    </p>
                )}
            </>
        );
    }

    const suggestionReadout = suggested != null ? stripsReadout(suggested, exactRow.stripSize) : null;

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h4 className="text-xs font-bold uppercase tracking-wide text-[var(--ops-carbon-500)]">Stock in eVital, batch-wise</h4>
                <div className="flex items-center gap-4">
                    {emptyBatchCount > 0 && (
                        <label className="inline-flex items-center gap-1.5 text-xs text-[var(--ops-carbon-700)] cursor-pointer">
                            <input
                                type="checkbox"
                                className="w-3.5 h-3.5"
                                checked={showEmptyBatches}
                                onChange={(event) => setShowEmptyBatches(event.target.checked)}
                            />
                            {`Show 0-stock batches (${emptyBatchCount})`}
                        </label>
                    )}
                    <button
                        type="button"
                        onClick={() => refetch()}
                        disabled={isFetching}
                        className="text-xs font-semibold text-[var(--ops-emerald-600)] hover:underline disabled:opacity-50"
                    >
                        {isFetching ? "Reading…" : "Re-read stock"}
                    </button>
                </div>
            </div>

            {/* The actionable bit first: what this stock says to order. */}
            {!isLoading && !isError && (
                suggested != null ? (
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[var(--ops-emerald-200)] bg-[var(--ops-emerald-50)] px-3 py-2">
                        <span className="text-sm font-semibold text-[var(--ops-carbon-900)]">
                            {`Suggested from this stock: ${suggested} tablets`}
                            {suggestionReadout ? ` — ${suggestionReadout}` : ""}
                            {line.view !== "reorder" && exactRow.stripSize ? ` (incl. 1 spare strip, ${exactRow.stripSize})` : ""}
                        </span>
                        {onUseSuggested && (
                            <button
                                type="button"
                                onClick={() => onUseSuggested(suggested)}
                                className="px-3 py-1.5 rounded-lg bg-[var(--ops-emerald-600)] text-[var(--ops-carbon-25)] text-xs font-semibold"
                            >
                                Use suggested
                            </button>
                        )}
                    </div>
                ) : (
                    <p className="mt-3 rounded-lg bg-[var(--ops-amber-50)] px-3 py-2 text-xs text-[var(--ops-amber-700)]">
                        No suggestion — stock or demand couldn&apos;t be worked out. Enter the quantity to order.
                    </p>
                )
            )}

            {!isLoading && !isError && exactRow && (
                <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div>
                        <dt className="text-xs text-[var(--ops-carbon-500)]">Quantity needed</dt>
                        <dd className="font-semibold">{exactRow.demandUnknown ? <EmptyValue /> : getViewDemand(exactRow, line.view, line.lapsingEnabled)}</dd>
                    </div>
                    <div>
                        <dt className="text-xs text-[var(--ops-carbon-500)]">Qty present</dt>
                        <dd className="font-semibold">{stock.onHand !== null ? stock.onHand : <EmptyValue />}</dd>
                    </div>
                    <div>
                        <dt className="text-xs text-[var(--ops-carbon-500)]">In transit</dt>
                        <dd className="font-semibold">{exactRow.inTransit ?? 0}</dd>
                    </div>
                    <div>
                        <dt className="text-xs text-[var(--ops-carbon-500)]">Strip size</dt>
                        <dd className="font-semibold">{exactRow.stripSize != null ? `${exactRow.stripSize} / strip` : <EmptyValue />}</dd>
                    </div>
                </dl>
            )}

            <div className="mt-3">{body}</div>
        </div>
    );
}

BatchStockPanel.propTypes = {
    line: PropTypes.shape({
        itemInventoryId: PropTypes.string,
        view: PropTypes.string,
        lapsingEnabled: PropTypes.bool,
        lookaheadDays: PropTypes.number,
    }).isRequired,
    // Only in the cart, where a suggestion can fill the line's quantity; read-only elsewhere.
    onUseSuggested: PropTypes.func,
};

BatchStockPanel.defaultProps = { onUseSuggested: undefined };

export default BatchStockPanel;
